import { Divider, Headline, IconButton, Text, TextButton } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { PriceSectionStyle } from '~/app/pages/summary/dialogs/price-detail/components/price-section-style';

import {
	ButtonsStyle,
	HeaderStyle,
	OverviewStyle,
	PriceDetailsStyle,
} from '~/app/compositions/content/service-footer/components/price-details/price-details-style';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { PriceSection } from '~/app/pages/summary/dialogs/price-detail/components/price-section';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';
import { FootnoteRenderer, useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const PriceDetailsLegalFi = () => {
	const { setOpenPriceDetails } = useCtxConfigurator();
	const { i18n } = useI18n();
	return (
		<PriceDetailsStyle>
			<div className="close-button">
				<IconButton
					variant={'tertiary'}
					icon={'arrow-down'}
					size={'md'}
					onClick={() => setOpenPriceDetails(false)}
					aria-label={i18n('ngc.actions.showPriceDetails')}
				/>
			</div>
			<div className="content">
				<Header />
				<Overview />
				<FootnoteRenderer />
				<Buttons />
			</div>
		</PriceDetailsStyle>
	);
};

const Header = () => {
	const { config } = useMarketConfig();
	const { catalog } = useConfigurator();
	const { getReference } = useFootnotes();
	const { i18n } = useI18n();

	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	return (
		<HeaderStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
				{catalog.carInfo?.carline}
			</Headline>
			<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
				{catalog.carInfo?.model}
				{!config.hideConsumptionEmissionEngineName && (
					<FootnoteReferenceNgc
						reference={getReference(CAE_CONFIGUREDCAR_REMARK)}
						ariaLabel={i18n('ngc.a11y.footnotes.cae')}
					/>
				)}
			</Text>
		</HeaderStyle>
	);
};

const Overview = () => {
	const { catalog, carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { priceDateRemark } = usePriceDetailRemarks();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const priceParts = useMemo(() => {
		return getFilteredPriceParts(priceSummary, config);
	}, [priceSummary?.parts, config.uiPreferences?.prices?.hiddenPriceParts]);

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const isBevModel = useMemo(() => {
		if (!carType) return undefined;
		return carType === 'BEV';
	}, [carType]);

	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	return (
		<OverviewStyle>
			<Headline tag="h2" variant={{ type: 'headline', order: '4', weight: 'bold' }}>
				{i18n('ngc.priceDetails.title')}
			</Headline>
			<div className="prices">
				{priceParts
					?.filter((p) => !(isBevModel && p.type.includes('taxableBenefit')))
					.map((part) => (
						<PriceSection
							key={part.type}
							pricePartType={part.type}
							title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
							price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
							disclaimer={null}
						/>
					))}
			</div>
			<Divider />
			<PriceSectionFsag
				pricePartType="total"
				title={i18n('ngc.priceDetails.total')}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
				disclaimer={priceDisclaimerText}
			/>
			<div className="prices">
				{priceParts
					?.filter((p) => isBevModel && p.type.includes('taxableBenefit'))
					.map((part) => (
						<PriceSection
							key={part.type}
							pricePartType={part.type}
							title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
							price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
							disclaimer={null}
						/>
					))}
			</div>
			{priceDateRemark && (
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
		</OverviewStyle>
	);
};

const Buttons = () => {
	const [, setCurrentView] = useAtom(state.currentView);
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { setOpenPriceDetails } = useCtxConfigurator();

	const handleClickSummary = useCallback(() => {
		const body = document?.getElementsByTagName('body');
		body[0].style.overflow = 'auto';
		setOpenPriceDetails(false);
		setCurrentView('summary');
		appTrackingService.basketNavigationClick({
			label: i18n('ngc.actions.getYourCar'),
			targetUrl: '#summary',
			targetPage: 'summary',
		});
	}, [i18n, setCurrentView]);

	const handleClickStockCar = () => {
		setOpenPriceDetails(false);
		showStockCarDialog();
	};

	return (
		<ButtonsStyle>
			{StockCarEnabled() && (
				<TextButton
					className="main-cta-button"
					type={'button'}
					variant={'secondary'}
					onClick={handleClickStockCar}
					size={'sm'}
					isFullWidth
					aria-label={i18n('ngc.stockcar.trigger')}
					data-testid={DATA_TEST_ID.STOCK_CAR_BUTTON}>
					{i18n('ngc.stockcar.trigger')}
				</TextButton>
			)}
			<TextButton
				href={'#summary'}
				variant="primary"
				onClick={handleClickSummary}
				size="sm"
				data-testid={DATA_TEST_ID.SUMMARY_BUTTON}>
				{i18n('ngc.actions.getYourCar')}
			</TextButton>
		</ButtonsStyle>
	);
};

const PriceSectionFsag = ({
	pricePartType,
	title,
	price,
	bold,
	disclaimer,
}: {
	pricePartType: string;
	/** Translated title of this section.  */
	title: string;
	/** Formatted price of this part. */
	price: string | null | undefined;
	bold?: boolean;
	disclaimer: string | null | undefined;
}) => {
	const { i18n, i18nKeys } = useI18n();
	const { addFootnote, getReference } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;

	if (i18nKey in i18nKeys) {
		addFootnote(i18nKey, i18n(i18nKey));
	}

	if (disclaimer) {
		addFootnote('p-disclaimer', disclaimer);
	}

	return (
		<PriceSectionStyle>
			<Text tag="p" variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{title}
				<FootnoteReferenceNgc
					reference={getReference(i18nKey)}
					ariaLabel={i18n('ngc.a11y.footnotes.price.part', { part: title })}
				/>
				<FootnoteReferenceNgc
					reference={getReference('p-disclaimer')}
					ariaLabel={i18n('ngc.a11y.footnotes.price.disclaimer')}
				/>
			</Text>
			<Text tag="p" variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{price}
			</Text>
		</PriceSectionStyle>
	);
};

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n, i18nKeys } = useI18n();

	return {
		priceDateRemark: 'ngc.summary.remark' in i18nKeys ? i18n('ngc.summary.remark') : null,
	};
}
